<script setup lang="ts">
  import { computed } from "vue";
  import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import { Select } from "@/components/episto-ui/select";
  import { Switch } from "@/components/ui/switch";
  import type { SelectOption } from "@/components/episto-ui/select";
  import type { SurveyExportPayload } from "@/types";

  const form = defineModel<SurveyExportPayload>("form", { required: true });

  const props = defineProps<{
    questionNameOptions: SelectOption[];
    showCodification: boolean;
    showEmail: boolean;
    showAdminFields: boolean;
    showDisplay: boolean;
    isExcel: boolean;
    isAdmin: boolean;
  }>();

  const questionNames = computed<string[]>({
    get: () => (form.value.question_names ? form.value.question_names.split(",").filter(Boolean) : []),
    set: (names) => (form.value.question_names = names.join(",")),
  });

  const displayOptions = [
    { value: "title", label: "Title" },
    { value: "code", label: "Code" },
  ];
  const multipleChoiceOptions = computed(() => [
    { value: "compact", label: "Compact (single column with ; separator)" },
    { value: "expand", label: "Expand (one column per answer with 0/1 codes)" },
    ...(props.isAdmin
      ? [
          { value: "csv_xml", label: "csv_xml" },
          { value: "update_answers", label: "update_answers" },
          { value: "is_pii", label: "is_pii" },
        ]
      : []),
  ]);
</script>

<template>
  <AccordionItem value="questions">
    <AccordionTrigger class="text-base font-semibold">Questions</AccordionTrigger>
    <AccordionContent>
      <div class="space-y-6 pt-2">
        <div class="space-y-2">
          <Label>Filter questions</Label>
          <Select
            v-model="questionNames"
            :options="questionNameOptions"
            multiple
            filterable
            clearable
            placeholder="All questions"
            class="w-full" />
        </div>
        <div
          v-if="showCodification"
          class="flex items-center justify-between gap-4">
          <Label class="cursor-pointer">Include AI codification</Label>
          <Switch v-model="form.codification" />
        </div>
        <div
          v-if="showEmail"
          class="flex items-center justify-between gap-4">
          <Label class="cursor-pointer">Include email</Label>
          <Switch v-model="form.include_emails" />
        </div>
        <template v-if="showAdminFields">
          <div class="space-y-2">
            <Label>
              Survey customer IDs
              <span class="font-normal text-muted-foreground">(admin, comma-separated)</span>
            </Label>
            <Input
              v-model="form.survey_customer_ids"
              placeholder="id1,id2" />
          </div>
          <div class="space-y-2">
            <Label>
              User attributes
              <span class="font-normal text-muted-foreground">(admin, comma-separated)</span>
            </Label>
            <Input
              v-model="form.user_attributes"
              placeholder="attr1,attr2" />
          </div>
        </template>
      </div>
    </AccordionContent>
  </AccordionItem>

  <AccordionItem
    v-if="showDisplay"
    value="display">
    <AccordionTrigger class="text-base font-semibold">Display options</AccordionTrigger>
    <AccordionContent>
      <div class="space-y-6 pt-2">
        <div class="space-y-2">
          <Label>Answer label</Label>
          <Select
            v-model="form.display"
            :options="displayOptions"
            class="w-full" />
        </div>
        <div
          v-if="isExcel"
          class="space-y-2">
          <Label>Multiple choice display</Label>
          <Select
            v-model="form.mode"
            :options="multipleChoiceOptions"
            class="w-full" />
        </div>
      </div>
    </AccordionContent>
  </AccordionItem>
</template>
